import { Illustration, Text, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Box } from 'native-base';
import React, { useEffect } from 'react';
import { FlatList } from 'react-native';

import { TableHeadContainer, TableHeadItem } from '../../../../components/TableHead';
import { useDynamicOrdinationContext } from '../../../../contexts/DynamicOrdination';
import { useScrollCloseToBottomContext } from '../../../../contexts/ScrollCloseToBottom';
import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import { AssetsLoading } from '../../../BatchTicket/components/DynamicAsset/components/AssetsLoading';
import {
  OrderHistoryTableProps,
  ordersHistoryTableHead,
  OrderHistoryItemProps,
} from '../constants';

import OrderHistoryItem from './OrderHistoryItem';
import { OrderHistoryItemLoading } from './OrderHistoryItemLoading';

const NotFoundTable = () => (
  <VStack alignItems="center" paddingY={Spacing['stack.xxxl']}>
    <VStack alignItems="center" space={Spacing['stack.quarck']}>
      <Illustration name="NotEmptyIllustration" testID="empty-illustration" />
      <Text color="neutral.black" variant="semibold">
        Você ainda não enviou nenhuma ordem
      </Text>
      <Text color="neutral.black">Lembre-se de usar a boleta fácil para enviar suas ordens.</Text>
    </VStack>
  </VStack>
);

const NotFoundTableSearchTerm = () => (
  <VStack alignItems="center" paddingY={Spacing['stack.xxxl']}>
    <VStack alignItems="center" space={Spacing['stack.quarck']}>
      <Illustration name="NotFoundIllustration" testID="empty-illustration" />
      <Text color="neutral.black" variant="semibold">
        Nenhum resultado corresponde à sua pesquisa
      </Text>
      <Text color="neutral.black">Por favor, verifique o campo informado e tente novamente.</Text>
    </VStack>
  </VStack>
);

export const OrderHistoryTable = (props: OrderHistoryTableProps) => {
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { isCloseToBottom } = useScrollCloseToBottomContext();

  const { appliedOrderBy, handleAutoOrderBy } = useDynamicOrdinationContext();

  useEffect(() => {
    if (isCloseToBottom && !props.isLoading.all && !props.isLoading.more) {
      props.listMoreOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCloseToBottom]);

  if (props.data.orders.length === 0 && !props.isLoading.all) {
    if (props.searchTerm || props.hasFilter) {
      return <NotFoundTableSearchTerm />;
    }
    return <NotFoundTable />;
  }

  const data = props.data.orders;

  const handleDetailsPress = (item: OrderHistoryItemProps) => {
    navigate('OrderHistoryModal', { item });
  };

  const renderPrincipal = () => {
    if (props.isLoading.all) {
      return <AssetsLoading ProductItemLoading={OrderHistoryItemLoading} quantity={5} />;
    }
    return (
      <FlatList
        data={data}
        windowSize={10}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={5}
        keyExtractor={(_, index) => index.toString()}
        ItemSeparatorComponent={() => (
          <Box style={{ height: Utils.Token.ToInt(Spacing, 'stack.nano') }} />
        )}
        showsVerticalScrollIndicator={false}
        onEndReachedThreshold={1.5}
        onEndReached={props.listMoreOrders}
        ListFooterComponent={() => (props.isLoading.more ? <OrderHistoryItemLoading /> : null)}
        renderItem={({ item }) => (
          <OrderHistoryItem item={item} onSelect={() => handleDetailsPress(item)} />
        )}
      />
    );
  };

  if (props.isMobile) {
    return (
      <VStack space={Spacing['stack.xxs']}>
        {props.isLoading.all ? (
          Array.from({ length: 3 }).map((_, index) => (
            <OrderHistoryItemLoading key={index.toString()} />
          ))
        ) : (
          <>
            {data.map((item) => (
              <OrderHistoryItem
                key={item.orderId}
                item={item}
                onSelect={() => handleDetailsPress(item)}
              />
            ))}
          </>
        )}
        {!!props.isLoading.more &&
          Array.from({ length: 1 }).map((_, index) => (
            <OrderHistoryItemLoading key={index.toString()} />
          ))}
      </VStack>
    );
  }

  return (
    <VStack maxH="700px">
      <TableHeadContainer px={Spacing['inline.xxxs']}>
        {ordersHistoryTableHead.map((item, index) => (
          <TableHeadItem
            key={`${item.title}-${index}`}
            {...item}
            value={item.orderValue}
            orderBy={appliedOrderBy}
            onPress={() => handleAutoOrderBy(item.orderValue as string)}
          />
        ))}
      </TableHeadContainer>
      {renderPrincipal()}
    </VStack>
  );
};
